import { escapeHtmlText, imageTypeOf, mapImageExtension, mapsShowing, fogged, slugify, type ExportData, type ExportEntry, type ExportMap, type ExportStory } from '@realmery/realm-core';
import { MAP_CSS, mapFigure, placesList, type MapHrefs } from './mapFigure.ts';
import { relinkMentions } from './markdown.ts';
import { sanitizeEntryHtmlToFragment } from '../security/sanitize.ts';

/**
 * The static website (docs/PARITY-STEP-6-PLAN.md section 3), as the desktop's
 * `export.StaticSiteExporter` writes its folder: `index.html`, a page per entry in
 * `entries/` with its pictures, a page per map in `maps/` with its picture, an Owner's
 * stories in `stories/`, and `style.css`. The page zips it.
 *
 * What's in it is chosen by audience: an entry or collection is in if it's at least as
 * public as the audience chosen; a custom audience only on an Owner's site; a link to an
 * entry that's left out says "(restricted)", so the site never shows what it hides.
 * Below Owner a map is the players' view: GM-only places, hidden layers and what's under
 * the fog are left out, and the fog is painted into its picture (`fogPictures`, drawn by
 * the page), so nothing under it can be recovered from the files.
 *
 * Differences, for safety, from what the desktop writes, as in the HTML export: entry
 * text goes through the entry view's sanitizer, mention links point at their entry's page
 * (or become plain names), pictures are written only when their bytes are an image, and
 * each page carries a policy that runs no scripts and loads nothing from elsewhere.
 */

/** `Audience`, from the most private to the most public; `CUSTOM` is set apart. */
export const SITE_AUDIENCES = ['OWNER', 'EDITOR', 'CONTRIBUTOR', 'PLAYER', 'READER'] as const;
export type SiteAudience = (typeof SITE_AUDIENCES)[number];
const ORDER = [...SITE_AUDIENCES, 'CUSTOM'];

/** Files only: its own stylesheet and pictures, and the pictures embedded in a map's icons. */
export const SITE_POLICY = "default-src 'none'; img-src 'self' file: data:; style-src 'self' file:; base-uri 'none'; form-action 'none'";

const STYLE = `body { font-family: Georgia, 'Times New Roman', serif; max-width: 860px; margin: 2rem auto; padding: 0 1.5rem; line-height: 1.5; color: #222; }
h1 { border-bottom: 3px solid #6b4f2a; padding-bottom: 0.4rem; }
h2 { color: #6b4f2a; margin-top: 2rem; }
.entry-type { font-style: italic; color: #666; margin-top: -0.5rem; }
table.metadata { border-collapse: collapse; margin: 0.75rem 0; }
table.metadata th, table.metadata td { border: 1px solid #ccc; padding: 0.3rem 0.6rem; text-align: left; }
.gallery img { max-width: 220px; margin: 0.5rem; border: 1px solid #ccc; }
`;

/** `isVisible`: everything for the Owner; below, at least as public, and never a custom audience. */
export function inSite(role: string, minimum: SiteAudience): boolean {
  if (minimum === 'OWNER') return true;
  const at = ORDER.indexOf(role);
  return role !== 'CUSTOM' && at >= ORDER.indexOf(minimum);
}

/** The picture a map's page links to (`imageName`): the fogged JPEG for players, else the original. */
export const siteMapImageName = (m: ExportMap, includeHidden: boolean) => `${m.slug}.${fogged(m, includeHidden) ? 'jpg' : mapImageExtension(m)}`;

const words = (n: number) => `${n.toLocaleString('en-US')} words`;
const encoder = new TextEncoder();

type Make = <K extends keyof HTMLElementTagNameMap>(parent: Element, tag: K, text?: string, className?: string) => HTMLElementTagNameMap[K];

/** A page: its own document, written with its policy and stylesheet. */
function page(title: string, stylesheet: string, build: (doc: Document, body: HTMLElement, el: Make) => void): Uint8Array {
  const doc = document.implementation.createHTMLDocument('');
  const el: Make = (parent, tag, text, className) => {
    const child = doc.createElement(tag);
    if (text !== undefined) child.textContent = text;
    if (className) child.className = className;
    parent.append(child);
    return child;
  };
  build(doc, doc.body, el);
  const head = [
    '<meta charset="UTF-8">',
    `<meta http-equiv="Content-Security-Policy" content="${SITE_POLICY}">`,
    '<meta name="referrer" content="no-referrer">',
    `<title>${escapeHtmlText(title)}</title>`,
    `<link rel="stylesheet" href="${stylesheet}">`,
  ].join('\n');
  return encoder.encode(`<!DOCTYPE html>\n<html lang="en">\n<head>\n${head}\n</head>\n${doc.body.outerHTML}\n</html>\n`);
}

const backLink = (body: HTMLElement, el: Make) => {
  el(el(body, 'p'), 'a', '← Back to index').setAttribute('href', '../index.html');
};

/** `safeImageName`: anything but letters, digits, `.`, `_` and `-` as `_`; the picture's id if nothing's left. */
const safeImageName = (fileName: string, id: string) => {
  const name = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
  return name.trim() === '' ? id : name;
};

/**
 * Every file of the site, by path. `fogPictures` holds, by slug, each fogged map's picture
 * with the fog painted in, for a site below Owner (see `renderFoggedJpeg`).
 */
export function staticSiteFiles(data: ExportData, minimum: SiteAudience, fogPictures: ReadonlyMap<string, Uint8Array> = new Map()): Map<string, Uint8Array> {
  const includeHidden = minimum === 'OWNER';
  const entries = data.entries.filter((e) => inSite(e.audienceRole, minimum));
  const collections = data.collections.filter((c) => inSite(c.audienceRole, minimum));
  // `uniqueFileName`: the title's slug, then -2, -3... among the entries in the site.
  const fileNames = new Map<string, string>();
  const taken = new Set<string>();
  for (const entry of entries) {
    const base = slugify(entry.title);
    let candidate = base;
    for (let n = 2; taken.has(`${candidate}.html`); n++) candidate = `${base}-${n}`;
    taken.add(`${candidate}.html`);
    fileNames.set(entry.id, `${candidate}.html`);
  }
  const files = new Map<string, Uint8Array>();
  files.set('style.css', encoder.encode(STYLE + MAP_CSS));
  files.set('index.html', indexPage(data, entries, collections, fileNames, includeHidden));
  for (const entry of entries) entryPage(data, entry, fileNames, includeHidden, files);
  for (const map of data.atlas.maps) mapPage(data, map, fileNames, includeHidden, fogPictures, files);
  if (includeHidden) for (const story of data.stories) files.set(`stories/${story.slug}.html`, storyPage(story, fileNames));
  return files;
}

function indexPage(data: ExportData, entries: readonly ExportEntry[], collections: ExportData['collections'], fileNames: ReadonlyMap<string, string>, includeHidden: boolean): Uint8Array {
  return page(data.realm.name, 'style.css', (_doc, body, el) => {
    el(body, 'h1', data.realm.name);
    if (data.realm.description.trim() !== '') el(body, 'p', data.realm.description, 'realm-description');
    if (data.atlas.maps.length > 0) {
      el(body, 'h2', 'Maps');
      const cards = el(body, 'div', undefined, 'map-cards');
      for (const map of data.atlas.maps) {
        const card = el(cards, 'a', undefined, 'map-card');
        card.setAttribute('href', `maps/${map.slug}.html`);
        const img = el(card, 'img');
        img.setAttribute('src', `maps/${siteMapImageName(map, includeHidden)}`);
        img.setAttribute('alt', map.map.name);
        el(card, 'div', map.map.name);
      }
    }
    if (includeHidden && data.stories.length > 0) {
      el(body, 'h2', 'Stories');
      const list = el(body, 'ul');
      for (const story of data.stories) {
        const item = el(list, 'li');
        el(item, 'a', story.name).setAttribute('href', `stories/${story.slug}.html`);
        item.append(` - ${words(story.words)}`);
      }
    }
    el(body, 'h2', 'Entries');
    const list = el(body, 'ul');
    for (const entry of entries) el(el(list, 'li'), 'a', `${entry.title} (${entry.typeName})`).setAttribute('href', `entries/${fileNames.get(entry.id)}`);
    if (collections.length > 0) {
      el(body, 'h2', 'Collections');
      for (const collection of collections) {
        const section = el(body, 'div', undefined, 'collection');
        el(section, 'h3', collection.name);
        const members = el(section, 'ul');
        for (const member of collection.members) {
          const file = fileNames.get(member.id);
          if (file) el(el(members, 'li'), 'a', member.title).setAttribute('href', `entries/${file}`);
        }
      }
    }
  });
}

function entryPage(data: ExportData, entry: ExportEntry, fileNames: ReadonlyMap<string, string>, includeHidden: boolean, files: Map<string, Uint8Array>): void {
  const fileName = fileNames.get(entry.id)!;
  const folder = fileName.slice(0, -'.html'.length);
  files.set(
    `entries/${fileName}`,
    page(entry.title, '../style.css', (doc, body, el) => {
      backLink(body, el);
      el(body, 'h1', entry.title);
      el(body, 'p', entry.typeName, 'entry-type');
      if (entry.labels.length > 0) el(body, 'p', `Labels: ${entry.labels.join(', ')}`, 'labels');
      if (entry.metadata.length > 0) {
        const table = el(body, 'table', undefined, 'metadata');
        for (const [key, value] of entry.metadata) {
          const row = el(table, 'tr');
          el(row, 'th', key);
          el(row, 'td', value);
        }
      }
      const content = el(body, 'div', undefined, 'content');
      if (entry.locked) {
        el(el(content, 'p'), 'i', '🔒 Content is locked.');
      } else {
        const fragment = doc.importNode(sanitizeEntryHtmlToFragment(entry.contentHtml), true);
        relinkMentions(fragment, (id) => fileNames.get(id) ?? null);
        content.append(fragment);
      }
      if (entry.links.length > 0) {
        el(body, 'h2', 'Relationships');
        const list = el(body, 'ul');
        for (const link of entry.links) {
          const item = el(list, 'li');
          item.append(`${link.relationshipType}: `);
          const target = fileNames.get(link.targetEntryId);
          if (target) el(item, 'a', link.targetTitle).setAttribute('href', target);
          else item.append('(restricted)');
        }
      }
      const onMaps = mapsShowing(data.atlas, entry.id, includeHidden);
      if (onMaps.length > 0) {
        el(body, 'h2', 'Appears on');
        const maps = el(body, 'ul');
        for (const map of onMaps) el(el(maps, 'li'), 'a', `🗺 ${map.map.name}`).setAttribute('href', `../maps/${map.slug}.html`);
      }
      const pictures = entry.locked ? [] : entry.images.filter((image) => imageTypeOf(image.data) !== null);
      if (pictures.length > 0) {
        el(body, 'h2', 'Images');
        const gallery = el(body, 'div', undefined, 'gallery');
        for (const image of pictures) {
          const name = safeImageName(image.fileName, image.id);
          files.set(`entries/images/${folder}/${name}`, image.data);
          const img = el(gallery, 'img');
          img.setAttribute('src', `images/${folder}/${name}`);
          img.setAttribute('alt', image.fileName);
        }
      }
    }),
  );
}

function mapPage(data: ExportData, map: ExportMap, fileNames: ReadonlyMap<string, string>, includeHidden: boolean, fogPictures: ReadonlyMap<string, Uint8Array>, files: Map<string, Uint8Array>): void {
  const imageName = siteMapImageName(map, includeHidden);
  files.set(`maps/${imageName}`, fogged(map, includeHidden) ? (fogPictures.get(map.slug) ?? new Uint8Array()) : map.map.imageData);
  const hrefs: MapHrefs = {
    entry: (id) => {
      const file = fileNames.get(id);
      return file ? `../entries/${file}` : null;
    },
    map: (id) => {
      const target = data.atlas.maps.find((m) => m.map.id === id);
      return target ? `${target.slug}.html` : null;
    },
  };
  files.set(
    `maps/${map.slug}.html`,
    page(map.map.name, '../style.css', (doc, body, el) => {
      backLink(body, el);
      el(body, 'h1', map.map.name);
      body.append(mapFigure(doc, data.atlas, map, hrefs, includeHidden, imageName));
      el(body, 'h2', 'Places');
      body.append(placesList(doc, data.atlas, map, hrefs, includeHidden));
    }),
  );
}

function storyPage(story: ExportStory, fileNames: ReadonlyMap<string, string>): Uint8Array {
  return page(story.name, '../style.css', (doc, body, el) => {
    backLink(body, el);
    el(body, 'h1', story.name);
    if (story.description.trim() !== '') el(body, 'p', story.description, 'realm-description');
    el(body, 'p', words(story.words), 'entry-type');
    let act: string | null = null;
    let chapter: string | null = null;
    for (const scene of story.scenes) {
      if (scene.actTitle !== act) {
        act = scene.actTitle;
        chapter = null;
        el(body, 'h2', act);
      }
      if (scene.chapterTitle !== chapter) {
        chapter = scene.chapterTitle;
        el(body, 'h3', chapter);
      }
      el(body, 'h4', scene.title);
      const fragment = doc.importNode(sanitizeEntryHtmlToFragment(scene.contentHtml), true);
      relinkMentions(fragment, (id) => {
        const file = fileNames.get(id);
        return file ? `../entries/${file}` : null;
      });
      el(body, 'div', undefined, 'content').append(fragment);
    }
  });
}

